<template>
  <article class="about">
    <header>
      <h2 class="h2 article-title">{{ title }}</h2>
    </header>
    <section class="about-text">
      <div class="content-card">
        <div class="error-container">
          <div class="error-icon">
            <ion-icon :name="errorIcon"></ion-icon>
          </div>
          <p class="error-code">{{ errorCode }}</p>
          <p>{{ message }}</p>
          <p v-if="showCurrentPath" class="error-path">
            Path: <span class="highlight">{{ currentPath }}</span>
          </p>
          <button class="error-button" @click="goHome">
            Return to Home
          </button>
          <button v-if="canRetry" class="error-button retry" @click="retryAction">
            Retry
          </button>
        </div>
      </div>
    </section>
  </article>
</template>

<script>
export default {
  name: 'ErrorFallback',
  props: {
    title: {
      type: String,
      default: 'Page Not Found'
    },
    message: {
      type: String,
      default: "Sorry, we couldn't find the page you're looking for. Please try navigating to the home page."
    },
    errorType: {
      type: String,
      default: 'not-found',
      validator: (value) => ['not-found', 'server-error', 'network-error', 'permission-denied', 'timeout', 'generic'].includes(value)
    },
    canRetry: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentPath() {
      return window.location.pathname;
    },
    showCurrentPath() {
      return this.currentPath && this.currentPath !== '/' && !this.currentPath.includes('index.html');
    },
    errorCode() {
      const codes = {
        'not-found': '404',
        'server-error': '500',
        'permission-denied': '403',
        'timeout': '408',
        'network-error': 'ERR_NETWORK',
        'generic': 'ERROR'
      };
      return codes[this.errorType] || '404';
    },
    errorIcon() {
      const icons = {
        'not-found': 'alert-circle-outline',
        'server-error': 'server-outline',
        'permission-denied': 'lock-closed-outline',
        'timeout': 'time-outline',
        'network-error': 'wifi-outline',
        'generic': 'warning-outline'
      };
      return icons[this.errorType] || 'alert-circle-outline';
    }
  },
  methods: {
    goHome() {
      window.history.pushState({}, '', '/');
      this.$emit('go-home');
    },
    retryAction() {
      this.$emit('retry');
      // If we have a network error, try reloading the page
      if (this.errorType === 'network-error' || this.errorType === 'timeout') {
        window.location.reload();
      }
    }
  }
};
</script>

<style scoped>
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.error-icon {
  font-size: 4rem;
  color: var(--bittersweet-shimmer);
  margin-bottom: 10px;
}

.error-code {
  font-size: 5rem;
  font-weight: var(--fw-600);
  background: var(--text-gradient-yellow);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  margin: 0 0 20px;
  line-height: 1;
}

.error-path {
  margin-top: 20px;
  font-family: monospace;
  background: rgba(0, 0, 0, 0.2);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: var(--fs-7);
  max-width: 100%;
  overflow-x: auto;
  white-space: nowrap;
}

.highlight {
  color: var(--orange-yellow-crayola);
}

.error-button {
  margin-top: 30px;
  background: var(--border-gradient-onyx);
  color: var(--orange-yellow-crayola);
  font-size: var(--fs-6);
  font-weight: var(--fw-500);
  padding: 12px 25px;
  border-radius: 14px;
  cursor: pointer;
  transition: var(--transition-1);
  border: none;
  outline: none;
  position: relative;
}

.error-button::before {
  content: "";
  position: absolute;
  inset: 1px;
  background: var(--bg-gradient-jet);
  border-radius: inherit;
  z-index: -1;
}

.error-button:hover {
  background: var(--bg-gradient-yellow-1);
}

.content-card {
  position: relative;
  background: var(--border-gradient-onyx);
  padding: 20px;
  border-radius: 14px;
  box-shadow: var(--shadow-2);
  z-index: 1;
}

.content-card::before {
  content: "";
  position: absolute;
  inset: 1px;
  background: var(--bg-gradient-jet);
  border-radius: inherit;
  z-index: -1;
}

@media (max-width: 580px) {
  .error-code {
    font-size: 4rem;
  }
  
  .error-path {
    font-size: var(--fs-8);
    padding: 6px 12px;
  }
}
</style>
